import React, { useState } from "react";
import {
  IconCalculator,
  IconPlus,
  IconSearch,
  IconCheck,
  IconFileSpreadsheet,
  IconHistory,
  IconBuildingBank,
  IconTrendingDown,
  IconAlertCircle,
  IconFileText
} from "@tabler/icons-react";
import {
  useDepreciationRuns,
  useDepreciationPreview,
  postDepreciationRun
} from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetDepreciationTab() {
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { data: runs, error, isLoading, mutate: mutateRuns } = useDepreciationRuns();
  const { data: previewData, isLoading: isPreviewLoading, mutate: mutatePreview } = useDepreciationPreview(
    selectedMonth,
    selectedYear
  );

  const months = [
    { value: 1, label: "January (Baishakh/Jan)" },
    { value: 2, label: "February (Jestha/Feb)" },
    { value: 3, label: "March (Ashadh/Mar)" },
    { value: 4, label: "April (Shrawan/Apr)" },
    { value: 5, label: "May (Bhadra/May)" },
    { value: 6, label: "June (Ashwin/Jun)" },
    { value: 7, label: "July (Kartik/Jul)" },
    { value: 8, label: "August (Mangsir/Aug)" },
    { value: 9, label: "September (Poush/Sep)" },
    { value: 10, label: "October (Magh/Oct)" },
    { value: 11, label: "November (Falgun/Nov)" },
    { value: 12, label: "December (Chaitra/Dec)" }
  ];

  const handlePostToGL = async () => {
    if (!previewData || previewData.length === 0) {
      toast.error("No eligible assets for depreciation in this period");
      return;
    }

    if (
      !window.confirm(
        `Post monthly depreciation for ${months.find((m) => m.value === selectedMonth)?.label} ${selectedYear} to General Ledger? This will write official double-entry journal entries.`
      )
    ) {
      return;
    }

    try {
      const res = await postDepreciationRun({
        period_month: selectedMonth,
        period_year: selectedYear,
        notes: `Monthly asset amortization run for ${selectedMonth}/${selectedYear}`
      });
      toast.success(res?.message || "Depreciation run posted to GL successfully!");
      setIsPreviewOpen(false);
      mutateRuns();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to post depreciation");
    }
  };

  const previewTotal = Array.isArray(previewData)
    ? previewData.reduce((acc, curr) => acc + Number(curr.depreciation_amount || 0), 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconCalculator className="w-6 h-6 text-restro-green" />
            Fixed Asset Depreciation & General Ledger Accounting
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Automate monthly Straight-Line and Reducing Balance asset write-downs with direct double-entry journal postings.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-medium"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>

          <input
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="w-24 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-semibold"
          />

          <button
            onClick={() => setIsPreviewOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
          >
            <IconCalculator className="w-4 h-4" />
            Calculate / Preview Run
          </button>
        </div>
      </div>

      {/* Historical Depreciation Runs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconHistory className="w-5 h-5 text-gray-400" />
            Posted Depreciation Runs & Journal Ledger References
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Run Number</th>
                <th className="px-6 py-4">Period</th>
                <th className="px-6 py-4">Execution Date</th>
                <th className="px-6 py-4">Assets Depreciated</th>
                <th className="px-6 py-4">Total Amount (NPR)</th>
                <th className="px-6 py-4">GL Journal Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-10 text-gray-400">
                    Loading depreciation records...
                  </td>
                </tr>
              ) : !runs || runs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-400">
                    <IconCalculator className="w-12 h-12 mx-auto mb-2 opacity-30 text-restro-green" />
                    No depreciation runs executed yet. Select a period above and click "Calculate / Preview Run".
                  </td>
                </tr>
              ) : (
                runs.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                    <td className="px-6 py-4 font-mono text-xs font-bold text-gray-900 dark:text-white">
                      {r.run_number || `DEP-${r.id}`}
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-800 dark:text-gray-200">
                      {months.find((m) => m.value === r.period_month)?.label?.split(" ")[0]} {r.period_year}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {r.run_date ? new Date(r.run_date).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-6 py-4 text-xs font-medium text-gray-700 dark:text-gray-300">
                      {r.asset_count || "Multiple"} Active Assets
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                      Rs. {Number(r.total_depreciation || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                        <IconBuildingBank className="w-3.5 h-3.5" />
                        Posted to Ledger
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Depreciation Calculation Preview */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <IconCalculator className="w-5 h-5 text-restro-green" />
                  Depreciation Calculation Preview ({months.find((m) => m.value === selectedMonth)?.label?.split(" ")[0]} {selectedYear})
                </h3>
                <div className="text-xs text-gray-400 mt-0.5">
                  Review calculations before posting official entries to General Ledger.
                </div>
              </div>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Journal Voucher Simulation Pill */}
              <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                  Double-Entry GL Impact:
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                    <span className="font-bold text-rose-600">DEBIT:</span> Depreciation Expense Account
                    <div className="text-base font-bold text-gray-900 dark:text-white mt-1">
                      Rs. {previewTotal.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                    <span className="font-bold text-emerald-600">CREDIT:</span> Accumulated Depreciation
                    <div className="text-base font-bold text-gray-900 dark:text-white mt-1">
                      Rs. {previewTotal.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Schedule Item List */}
              <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-100 dark:border-gray-700">
                <table className="w-full text-left text-xs text-gray-600 dark:text-gray-300">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase font-semibold sticky top-0">
                    <tr>
                      <th className="px-4 py-3">Asset</th>
                      <th className="px-4 py-3">Method</th>
                      <th className="px-4 py-3">Cost (NPR)</th>
                      <th className="px-4 py-3">Acc. Prior (NPR)</th>
                      <th className="px-4 py-3 text-right">Period Dep (NPR)</th>
                      <th className="px-4 py-3 text-right">Projected NBV</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {!previewData || previewData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-gray-400">
                          No depreciable active assets found for this period.
                        </td>
                      </tr>
                    ) : (
                      previewData.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-750">
                          <td className="px-4 py-2.5">
                            <div className="font-semibold text-gray-900 dark:text-white">{item.asset_name}</div>
                            <div className="font-mono text-[10px] text-gray-400">{item.asset_code}</div>
                          </td>
                          <td className="px-4 py-2.5">{item.depreciation_method || "Straight Line"}</td>
                          <td className="px-4 py-2.5">
                            Rs. {Number(item.purchase_cost || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5">
                            Rs. {Number(item.accumulated_depreciation || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-right font-bold text-rose-600 dark:text-rose-400">
                            Rs. {Number(item.depreciation_amount || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-right font-bold text-gray-900 dark:text-white">
                            Rs. {Number(item.new_net_book_value || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Close Preview
                </button>
                <button
                  type="button"
                  onClick={handlePostToGL}
                  disabled={!previewData || previewData.length === 0}
                  className="px-6 py-2.5 text-sm font-bold text-white bg-restro-green hover:bg-emerald-600 disabled:opacity-50 rounded-xl shadow transition flex items-center gap-2"
                >
                  <IconCheck className="w-4 h-4" />
                  Confirm & Post to General Ledger
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
